import { ReactElement, ReactNode } from 'react';

import { styled, useThemeProps } from '@mui/material/styles';
import Switch, { SwitchProps } from '@mui/material/Switch';

/**
 * What the two states mean, which sets their colours (theme: GratiIconSwitch):
 * - `neutral`: a choice between two named values (18 / 9 holes, ride / walk) — neither is "off".
 * - `yesNo`: yes / no, where "no" is a state worth noticing (playing / not playing).
 * - `visibility`: hidden / shown to members (draft / published) — the hidden state is the one to
 *   notice.
 */
export type GratiIconSwitchVariant = 'neutral' | 'yesNo' | 'visibility';

const GratiIconSwitchRoot = styled(Switch, {
  name: 'GratiIconSwitch',
  slot: 'root',
  shouldForwardProp: (prop) => prop !== 'ownerState',
  overridesResolver: (_props, styles) => styles.root,
})<{ ownerState: { variant: GratiIconSwitchVariant } }>(() => ({}));

const GratiSwitchThumbRoot = styled('span', {
  name: 'GratiIconSwitch',
  slot: 'thumb',
  overridesResolver: (_props, styles) => styles.thumb,
})(() => ({}));

const GratiSwitchThumbTextRoot = styled('span', {
  name: 'GratiIconSwitch',
  slot: 'thumbText',
  overridesResolver: (_props, styles) => styles.thumbText,
})(() => ({}));

/** The thumb disc: the thumb's size, filled with the switch colour, content in the surface colour. */
export function GratiSwitchThumb({ children }: { children: ReactNode }): ReactElement {
  return <GratiSwitchThumbRoot>{children}</GratiSwitchThumbRoot>;
}

/** Text content for a thumb, e.g. "18". */
export function GratiSwitchThumbText({ text }: { text: string }): ReactElement {
  return <GratiSwitchThumbTextRoot>{text}</GratiSwitchThumbTextRoot>;
}

export interface GratiIconSwitchProps extends Omit<SwitchProps, 'icon' | 'checkedIcon'> {
  /** Thumb content when unchecked. */
  icon: ReactNode;
  /** Thumb content when checked. */
  checkedIcon: ReactNode;
  /** Default: 'neutral'. */
  variant?: GratiIconSwitchVariant;
}

/**
 * A two-state switch whose thumb shows which state it is in: a disc in the switch colour holding
 * an icon or a short label. The base for every icon-thumb switch (GratiCheckSwitch,
 * GratiPublishSwitch); colours per variant live in the theme.
 */
export default function GratiIconSwitch(inProps: GratiIconSwitchProps): ReactElement {
  const { icon, checkedIcon, variant = 'neutral', ...props } = useThemeProps({ props: inProps, name: 'GratiIconSwitch' });
  return (
    <GratiIconSwitchRoot
      ownerState={{ variant }}
      icon={<GratiSwitchThumb>{icon}</GratiSwitchThumb>}
      checkedIcon={<GratiSwitchThumb>{checkedIcon}</GratiSwitchThumb>}
      {...props}
    />
  );
}
